import { useState } from "react";

import formatComma from "react-lib/utils/formatComma";

import { AlertV4, ButtonV4, FieldV4, ModalV4 } from "../common/ui";

import {
  REFUND_CHANNELS,
  refundErrorsOf,
  type RefundChannel,
  type RefundForm,
  type WalletCard,
} from "./billingWalletModelV4";

// Types
type ModalBillingRefundV4Props = {
  // data
  card: WalletCard;
  patient?: { hn?: string | null; full_name?: string } | null;
  busy?: boolean;
  error?: unknown;
  // callback
  onConfirm: (form: RefundForm) => void;
  onCloseError: () => void;
  onClose: () => void;
};

/** ยกเลิกและคืนเงินมัดจำ: คืนยอดคงเหลือทั้งหมดของการฝากครั้งนั้น */
const ModalBillingRefundV4 = (props: ModalBillingRefundV4Props) => {
  const [form, setForm] = useState<RefundForm>({
    channel: "cash",
    account: "",
    reason: "",
    username: "",
    password: "",
  });
  const [tried, setTried] = useState(false);

  const errors = tried ? refundErrorsOf(form) : [];
  const card = props.card;
  const bad = (empty: boolean) => (tried && empty ? " hv4-bill-pay-in--bad" : "");

  // Handler
  const handleChange = <K extends keyof RefundForm>(key: K, value: RefundForm[K]) => {
    setForm({ ...form, [key]: value });
  };

  const handleConfirm = () => {
    setTried(true);

    if (props.busy || refundErrorsOf(form).length > 0) {
      return;
    }

    props.onConfirm({ ...form, username: form.username.trim() });
  };

  // Render
  return (
    <ModalV4
      title="ยกเลิกและคืนเงินมัดจำ"
      width={860}
      tone="danger"
      className="hv4-bill-rfd-modal"
      headExtra={
        <span className="hv4-bill-dep-sub">
          {props.patient?.full_name || ""} ({props.patient?.hn || "-"})
        </span>
      }
      onClose={props.onClose}
    >
      <div className="hv4-bill-rfd">
        <div className="hv4-bill-rfd-wrap">
          <div className="hv4-bill-rfd-head">
            <span>เลขที่ใบรับมัดจำ</span>
            <span>รายการเงินมัดจำ</span>
            <span className="hv4-bill-rfd-num">ยอดฝาก</span>
            <span className="hv4-bill-rfd-num">ใช้ไปแล้ว</span>
            <span className="hv4-bill-rfd-num">คืนแล้ว</span>
            <span className="hv4-bill-rfd-num">คงเหลือ</span>
          </div>
          <div className="hv4-bill-rfd-row">
            <span>{card.group.receipt_no || "—"}</span>
            <b>{card.name}</b>
            <span className="hv4-bill-rfd-num">{formatComma(card.paid)}</span>
            <span className="hv4-bill-rfd-num">{formatComma(card.used)}</span>
            <span className="hv4-bill-rfd-num">{formatComma(card.refunded)}</span>
            <b className="hv4-bill-rfd-num hv4-bill-rfd-left">{formatComma(card.balance)}</b>
          </div>
        </div>

        {card.used > 0 && (
          <div className="hv4-bill-rfd-note">
            ⓘ Wallet นี้ใช้ไปแล้ว {formatComma(card.used)} บาท · คืนได้เฉพาะยอดคงเหลือ{" "}
            {formatComma(card.balance)} บาท (ยกเลิกบางส่วน)
          </div>
        )}

        <div className="hv4-bill-rfd-fields">
          <div>
            <span className="hv4-label">ยอดคืนเงิน (บาท)</span>
            <div className="hv4-input hv4-bill-rfd-amount">{formatComma(card.balance)}</div>
            <div className="hv4-bill-rfd-help">คืนยอดคงเหลือทั้งหมดของการฝากครั้งนี้</div>
          </div>

          <FieldV4
            label={
              <>
                ช่องทางคืนเงิน <b className="hv4-bill-pay-req">*</b>
              </>
            }
            htmlFor="hv4-rfd-channel"
          >
            <select
              id="hv4-rfd-channel"
              className="hv4-input"
              value={form.channel}
              onChange={(e) => handleChange("channel", e.target.value as RefundChannel)}
            >
              {REFUND_CHANNELS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </FieldV4>

          {form.channel === "transfer" && (
            <FieldV4
              label={
                <>
                  เลขที่บัญชี / ชื่อบัญชี <b className="hv4-bill-pay-req">*</b>
                </>
              }
              htmlFor="hv4-rfd-account"
            >
              <input
                id="hv4-rfd-account"
                className={`hv4-input${bad(!form.account.trim())}`}
                placeholder="เช่น 123-4-56789-0 น.ส. น้ำหนึ่ง"
                value={form.account}
                onChange={(e) => handleChange("account", e.target.value)}
              />
            </FieldV4>
          )}

          <FieldV4
            label={
              <>
                เหตุผลการยกเลิก <b className="hv4-bill-pay-req">*</b>
              </>
            }
            htmlFor="hv4-rfd-reason"
          >
            <input
              id="hv4-rfd-reason"
              className={`hv4-input${bad(!form.reason.trim())}`}
              value={form.reason}
              onChange={(e) => handleChange("reason", e.target.value)}
            />
          </FieldV4>

          <FieldV4
            label={
              <>
                ชื่อผู้ใช้ <b className="hv4-bill-pay-req">*</b>
              </>
            }
            htmlFor="hv4-rfd-user"
          >
            <input
              id="hv4-rfd-user"
              className={`hv4-input${bad(!form.username.trim())}`}
              autoComplete="off"
              value={form.username}
              onChange={(e) => handleChange("username", e.target.value)}
            />
          </FieldV4>

          <FieldV4
            label={
              <>
                รหัสผ่านยืนยัน <b className="hv4-bill-pay-req">*</b>
              </>
            }
            htmlFor="hv4-rfd-password"
          >
            <input
              id="hv4-rfd-password"
              type="password"
              className={`hv4-input${bad(!form.password)}`}
              autoComplete="off"
              placeholder="รหัสผ่านของผู้ทำรายการ"
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
            />
          </FieldV4>
        </div>

        {errors.length > 0 && (
          <div role="alert" className="hv4-bill-rfd-errors">
            <b>ยังทำรายการไม่ได้</b>
            <ul>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <AlertV4 tone="danger" error={props.error} onClose={props.onCloseError} />

        <div className="hv4-bill-rfd-foot">
          <span>หลังคืนเงิน Wallet นี้จะถูกยกเลิก (คงเหลือ 0)</span>
          <ButtonV4 variant="ghost" onClick={props.onClose}>
            ปิดหน้าต่าง
          </ButtonV4>
          <ButtonV4 variant="danger" disabled={props.busy} onClick={handleConfirm}>
            ยืนยันยกเลิกและคืนเงิน
          </ButtonV4>
        </div>
      </div>
    </ModalV4>
  );
};

ModalBillingRefundV4.displayName = "ModalBillingRefundV4";

export default ModalBillingRefundV4;
